import { useQuery } from '@tanstack/react-query';
import api from 'api';
import classNames from 'classnames';
import InputField from 'components/InputField';
import SelectField from 'components/SelectField';
import { SubmissionErrors } from 'final-form';
import InfoIcon from 'icons/Info-2.icon';
import { InventoryPlacementFormValues } from 'models/InventoryPlacement';
import { Button, Loader } from 'ncoded-component-library';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import './InventoryPlacementForm.styles.scss';

type InventoryPlacementFormProps = {
  selectedInventoryPlacement: InventoryPlacementFormValues | null;
  handleCancel?: () => void;
  onSubmit?: (
    values: InventoryPlacementFormValues,
  ) => SubmissionErrors | Promise<SubmissionErrors> | void;
};
const InventoryPlacementForm = (props: InventoryPlacementFormProps) => {
  const { selectedInventoryPlacement, handleCancel, onSubmit } = props;

  const { t } = useTranslation();

  const classes = classNames('manage-inventory-placement__form');

  const { data: locations, isLoading } = useQuery(
    ['locationsForPlacement'],
    async () => {
      const { data } = await api.inventoryLocations.getLocationsForPlacement();
      return data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const locationOptions: OptionValue[] = useMemo(
    () =>
      locations &&
      Object.values(locations).map((location) => ({
        label: {
          icon:
            location.availableToAdd || !!selectedInventoryPlacement ? null : (
              <>
                <InfoIcon
                  className="manage-inventory-placement__form__warning-icon"
                  data-tooltip-id="units"
                  data-tooltip-content={t('SingleUnitWarning')}
                />
                <Tooltip id="units" />
              </>
            ),
          text: `${location.binNo.toString()} - ${location.locationName}`,
        },
        searchValue: location.binNo.toString(),
        value: location.binNo.toString(),
      })),
    [locations, selectedInventoryPlacement, t],
  );

  const initialValues = useMemo(
    () => ({
      skuNo: selectedInventoryPlacement?.skuNo,
      binNo: selectedInventoryPlacement?.binNo.toString(),
      quantity: selectedInventoryPlacement?.quantity,
    }),
    [
      selectedInventoryPlacement?.binNo,
      selectedInventoryPlacement?.quantity,
      selectedInventoryPlacement?.skuNo,
    ],
  );
  const messages = useMemo(
    () => ({
      cancel: t('cancel'),
      skuNo: t('skuNo'),
      quantity: t('quantity'),
      update: t('update'),
      add: t('add'),
    }),
    [t],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={selectedInventoryPlacement ? initialValues : {}}
      render={({ handleSubmit, invalid, dirty }) => {
        const submitDisabled = invalid || !dirty;
        return isLoading ? (
          <Loader color="#007cb9" />
        ) : (
          <form className={classes} onSubmit={handleSubmit}>
            <SelectField
              name="binNo"
              options={locationOptions}
              label={t('InventoryLocations.titleClean')}
              disabled={!!selectedInventoryPlacement}
            />
            <InputField
              name="skuNo"
              label={messages.skuNo}
              placeholder={messages.skuNo}
              disabled={!!selectedInventoryPlacement}
            />
            <InputField
              placeholder={messages.quantity}
              label={messages.quantity}
              name="quantity"
            />
            <div className="manage-inventory-placement__form__actions">
              <Button
                type="button"
                onClick={handleCancel}
                variant="outline"
                className="manage-inventory-placement__form__actions__link"
              >
                <span>{messages.cancel} </span>
              </Button>

              <Button
                disabled={submitDisabled}
                type="submit"
                variant="solid"
                className="manage-inventory-placement__form__actions__submit"
              >
                {selectedInventoryPlacement ? messages.update : messages.add}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default InventoryPlacementForm;
